import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Collapse,
  Tooltip,
  TableSortLabel,
  Button
} from '@mui/material';
import { AiOutlineClose } from 'react-icons/ai';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { PiFileCsv } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';
import { fetchIdleCircuitsDetail } from '../../api/dashboardApi';
import DocumentViewer from '../DocumentHub/DocumentViewer';

const Row = ({ row }) => {
  const [open, setOpen] = useState(false);

  return (
    <React.Fragment>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.circuit_id || "-"}</TableCell>
        <TableCell>{row.service_id || "-"}</TableCell>
        <TableCell>{row.country || "-"}</TableCell>
        <TableCell>
          {row.location_name && row.location_id ? (
            <Tooltip title={row.location_name} arrow placement="top">
              <span style={{ cursor: "help", borderBottom: "1px dashed #9ca3af" }}>
                {row.location_id}
              </span>
            </Tooltip>
          ) : (
            row.location_id || "-"
          )}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.months_idle}</TableCell>
        <TableCell>{row.last_billed_month || "-"}</TableCell>
        <TableCell>{row.last_billed_invoice_id || "-"}</TableCell>
        <TableCell align="right">
          {row.last_billed_amount_usd != null 
            ? `$${Number(row.last_billed_amount_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
            : "-"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, backgroundColor: '#f1f5f9', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold', color: '#475569' }}>
                Invoice History
              </Typography>
              {row.invoice_ids && row.invoice_ids.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {row.invoice_ids.map((invId, idx) => (
                    <Paper key={idx} sx={{ px: 1.5, py: 0.5, backgroundColor: '#fff', border: '1px solid #cbd5e1', fontSize: '0.875rem' }} elevation={0}>
                      {invId}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">No invoice history available.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const descendingComparator = (a, b, orderBy) => {
  let valA = a[orderBy];
  let valB = b[orderBy];
  
  if (valA === null || valA === undefined) valA = "";
  if (valB === null || valB === undefined) valB = "";

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const IdleCircuitsDetailModal = ({ open, onClose, months }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('months_idle');

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open, months]);

  const { data: detailData, isLoading, isError } = useQuery({
    queryKey: ['idleCircuitsDetail', months],
    queryFn: () => fetchIdleCircuitsDetail(months),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const circuits = detailData?.data?.circuits || [];
  const totalCount = detailData?.data?.count || 0;

  const handleDownloadCSV = () => {
    if (!circuits || circuits.length === 0) return;
    try {
      const headers = [
        "Circuit ID",
        "Service ID",
        "Country",
        "Location Name",
        "Location ID",
        "Months Idle",
        "Last Billed Month",
        "Last Invoice ID",
        "Last Billed Amount ($)"
      ];
      const csvRows = [headers.join(",")];

      circuits.forEach(row => {
        const values = [
          `"${row.circuit_id || ""}"`,
          `"${row.service_id || ""}"`,
          `"${row.country || ""}"`,
          `"${row.location_name || ""}"`,
          `"${row.location_id || ""}"`,
          row.months_idle || 0,
          `"${row.last_billed_month || ""}"`,
          `"${row.last_billed_invoice_id || ""}"`,
          row.last_billed_amount_usd != null ? row.last_billed_amount_usd : ""
        ];
        csvRows.push(values.join(","));
      });

      const csvString = csvRows.join("\n");
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const fileName = `Idle_Circuits_Detail_${months}M_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute("download", fileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("CSV Download Error:", err);
    }
  };

  // Sorting and Pagination
  const sortedCircuits = [...circuits].sort(getComparator(order, orderBy));
  const paginatedCircuits = sortedCircuits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const renderSortableHeader = (label, property, align = "left") => (
    <TableCell align={align} sx={{ backgroundColor: "#7C2EA0", fontWeight: 700 }} sortDirection={orderBy === property ? order : false}>
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={createSortHandler(property)}
        sx={{
          color: '#fff !important',
          '& .MuiTableSortLabel-icon': { color: '#fff !important' },
          '&:hover': { color: '#e0e0e0 !important' }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "70vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        borderBottom: "1px solid #e0e0e0" 
      }}>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: "#7C2EA0" }}>
            Idle Circuits Detail
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {totalCount} circuits idle for ≥ {months} months (As of {detailData?.data?.as_of_month || '...'})
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Download CSV" placement="top" arrow>
            <span
              onClick={circuits && circuits.length > 0 ? handleDownloadCSV : undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                backgroundColor: "#f5f5f5",
                cursor: (circuits && circuits.length > 0) ? "pointer" : "default",
                opacity: (circuits && circuits.length > 0) ? 1 : 0.5,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (circuits && circuits.length > 0) {
                  e.currentTarget.style.backgroundColor = "#e0e0e0";
                }
              }}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
            >
              <PiFileCsv size={18} color="#333" />
            </span>
          </Tooltip>
          <IconButton onClick={onClose} sx={{ color: "#9e9e9e" }}>
            <AiOutlineClose />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: "#f8f9fa", display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
              <CircularProgress sx={{ color: "#7C2EA0" }} />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 4, textAlign: "center", color: "#EF4444" }}>
              Failed to load idle circuits detail.
            </Box>
          ) : circuits.length > 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <TableContainer sx={{ maxHeight: "55vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#7C2EA0", color: "#fff", width: 50 }} />
                      {renderSortableHeader("Circuit ID", "circuit_id")}
                      {renderSortableHeader("Service ID", "service_id")}
                      {renderSortableHeader("Country", "country")}
                      {renderSortableHeader("Location ID", "location_id")}
                      {renderSortableHeader("Months Idle", "months_idle", "right")}
                      {renderSortableHeader("Last Billed Month", "last_billed_month")}
                      {renderSortableHeader("Last Invoice ID", "last_billed_invoice_id")}
                      {renderSortableHeader("Last Billed Amount ($)", "last_billed_amount_usd", "right")}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCircuits.map((row, index) => (
                      <Row key={`${row.circuit_id}-${index}`} row={row} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={circuits.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          ) : (
            <Box sx={{ p: 4, textAlign: "center", color: "#6b7280" }}>
              No circuits found for this threshold.
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default IdleCircuitsDetailModal;
